import React from 'react';

/**
 * MiniSparkline
 * --------------
 * Lightweight inline SVG sparkline (no Recharts overhead). Renders a
 * polyline of `data` (number[]) plus an optional smooth area fill below.
 *
 * Used inside the Cornerstone Trend totals tiles so each tile shows the
 * 7-day rhythm at a glance, not just the rolled-up total.
 */
const MiniSparkline = ({
  data = [],
  color = '#22d3ee',
  width = 80,
  height = 22,
  strokeWidth = 1.5,
  testId,
}) => {
  if (!data || data.length < 2) {
    return (
      <div
        data-testid={testId}
        className="text-[11px] text-slate-400 italic"
        style={{ height }}
      >
        ~
      </div>
    );
  }

  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;

  // Build path coordinates with a 1px inset on top/bottom so the stroke
  // never clips against the bounding box.
  const stepX = width / (data.length - 1);
  const insetY = strokeWidth + 1;
  const usableH = height - insetY * 2;
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = insetY + (usableH - ((v - min) / range) * usableH);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  const linePath = `M ${points.join(' L ')}`;
  const areaPath = `${linePath} L ${width.toFixed(2)},${height} L 0,${height} Z`;

  const gradId = `spark-grad-${testId || color.replace('#', '')}`;

  return (
    <svg
      data-testid={testId}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="block"
    >
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Last-point dot for emphasis */}
      <circle
        cx={(data.length - 1) * stepX}
        cy={insetY + (usableH - ((data[data.length - 1] - min) / range) * usableH)}
        r={1.8}
        fill={color}
      />
    </svg>
  );
};

export default MiniSparkline;
